/**
 * How a document's picture sits among the words, Word's "Layout options": in line with the text
 * (a line of its own, aligned left, centred or right), square (beside the words, which wrap
 * around it on the other side), behind the text or in front of it (free on the page, moved by
 * dragging: `x`, `y` from where it is anchored). Its `width` is a share of the text's width.
 *
 * Pure: the editor's picture (`windows/graph/document/Editor.tsx`), the schema's copy for the
 * clipboard and the printed page all take their look from {@link pictureLook}, whose values are
 * checked numbers and known words only, so nothing typed reaches a style.
 */

/** The ways a picture may sit among the words, in the order offered. */
export const WRAPS = [
  { id: "inline", label: "In line with text" },
  { id: "square", label: "Square" },
  { id: "behind", label: "Behind text" },
  { id: "front", label: "In front of text" },
] as const;

/** How a picture sits among the words. */
export type Wrap = (typeof WRAPS)[number]["id"];

/** Where a picture lines up (square: the side it sits on, `center` meaning left). */
export const PICTURE_ALIGNS = ["left", "center", "right"] as const;

/** Where a picture lines up. */
export type PictureAlign = (typeof PICTURE_ALIGNS)[number];

/** The smallest and biggest width, in percent of the text's width. */
export const WIDTH_RANGE = [10, 100] as const;

/** How far a free picture may go from its anchor, in pixels of the paper. */
const MAX_OFFSET = 2000;

/** A picture's placement, checked. */
export interface PictureLook {
  wrap: Wrap;
  align: PictureAlign;
  /** Percent of the text's width. */
  width: number;
  /** From the anchor, in pixels of the paper (behind and in front only). */
  x: number;
  y: number;
}

/** Whether a picture of `wrap` is free on the page (behind or in front of the words). */
export function isFree(wrap: Wrap): boolean {
  return wrap === "behind" || wrap === "front";
}

/** `value` as a whole number within `low`..`high` (`fallback` when it is not a number). */
function within(value: unknown, low: number, high: number, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(Math.min(high, Math.max(low, n))) : fallback;
}

/** A picture's attributes read as a placement, anything unknown taken back to its default. */
export function pictureLook(attrs: Record<string, unknown>): PictureLook {
  const wrap = WRAPS.find((w) => w.id === attrs.wrap)?.id ?? "inline";
  const align = PICTURE_ALIGNS.find((a) => a === attrs.align) ?? "center";
  return {
    wrap,
    align,
    width: within(attrs.width, WIDTH_RANGE[0], WIDTH_RANGE[1], 100),
    x: within(attrs.x, -MAX_OFFSET, MAX_OFFSET, 0),
    y: within(attrs.y, -MAX_OFFSET, MAX_OFFSET, 0),
  };
}

/** The CSS of a picture's parts: the block holding it (`figure`) and the frame around the image. */
export interface PictureCss {
  figure: Record<string, string>;
  frame: Record<string, string>;
}

/** The CSS that places a picture as `look` says. */
export function pictureCss(look: PictureLook): PictureCss {
  const width = `${look.width}%`;
  switch (look.wrap) {
    case "square": {
      const side = look.align === "right" ? "right" : "left";
      return {
        figure: { float: side, width, margin: side === "left" ? "2pt 12pt 6pt 0" : "2pt 0 6pt 12pt", "text-align": "left" },
        frame: { width: "100%" },
      };
    }
    case "behind":
    case "front":
      // Anchored where the block is, taking no room: the words flow as if it were not there.
      return {
        figure: { position: "relative", height: "0", margin: "0", "z-index": look.wrap === "behind" ? "-1" : "3" },
        frame: { position: "absolute", left: `${look.x}px`, top: `${look.y}px`, width },
      };
    default:
      return { figure: { "text-align": look.align }, frame: { width } };
  }
}

/** `css` as a `style` attribute's text. */
export function styleText(css: Record<string, string>): string {
  return Object.entries(css)
    .map(([k, v]) => `${k}: ${v}`)
    .join("; ");
}
